import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, IconButton, Typography } from '@mui/material';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import type { FieldBaseProps } from './fields/types';

export interface BpStepperProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** Unit suffix shown next to the value (e.g. "days", "%"). */
  suffix?: string;
  min?: number;
  max?: number;
  /** Accessible label for the decrement button (translated). */
  decrementLabel?: string;
  /** Accessible label for the increment button (translated). */
  incrementLabel?: string;
}

/**
 * A number stepper with a label, help text, and optional unit suffix. RHF-bound;
 * `field.value` is a clamped integer. Used for the budget editor's timing /
 * exception-threshold values and the assignment-rule priority.
 */
export function BpStepper<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
  suffix,
  min = 0,
  max = 100000,
  decrementLabel = 'Decrease',
  incrementLabel = 'Increase',
}: BpStepperProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const num = Number.isFinite(Number(field.value)) ? Number(field.value) : min;
        const set = (n: number) => field.onChange(Math.max(min, Math.min(max, n)));
        return (
          <Box>
            {label && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
                {label}
              </Typography>
            )}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                overflow: 'hidden',
                height: 40,
              }}
            >
              <IconButton
                size="small"
                aria-label={`${decrementLabel} ${label ?? ''}`.trim()}
                disabled={disabled || num <= min}
                onClick={() => set(num - 1)}
                sx={{ borderRadius: 0, borderRight: '1px solid', borderColor: 'divider' }}
              >
                <RemoveOutlined sx={{ fontSize: 16 }} />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: '14px',
                  minWidth: 56,
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {num}
                </Typography>
                {suffix && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{suffix}</Typography>
                )}
              </Box>
              <IconButton
                size="small"
                aria-label={`${incrementLabel} ${label ?? ''}`.trim()}
                disabled={disabled || num >= max}
                onClick={() => set(num + 1)}
                sx={{ borderRadius: 0, borderLeft: '1px solid', borderColor: 'divider' }}
              >
                <AddOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            {helperText && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
                {helperText}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
